import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../console/api";
import { money } from "./FinanceDashboard";

const nzDate = (d) => new Intl.DateTimeFormat("en-CA", { timeZone: "Pacific/Auckland", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
const today = () => nzDate(new Date());
const blank = () => ({ type: "expense", category: "", amount: "", date: today(), note: "", party: "" });

export function FinanceEntries({ onChanged }) {
  const [entries, setEntries] = useState(null);
  const [cats, setCats] = useState({ income: [], expense: [] });
  const [form, setForm] = useState(blank());
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [e, c] = await Promise.all([api.get("/admin/finance/entries"), api.get("/admin/finance/categories")]);
    setEntries(e.data); setCats(c.data);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Couldn't load manual entries.")); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    const category = form.category === "__new__" ? newCat.trim() : form.category;
    if (!category) return toast.error("Pick or type a category.");
    if (!(Number(form.amount) > 0)) return toast.error("Amount must be more than zero.");
    setBusy(true);
    try {
      await api.post("/admin/finance/entries", { ...form, category, amount: Number(form.amount) });
      toast.success(`${form.type === "income" ? "Income" : "Expense"} of ${money(form.amount)} recorded.`);
      setForm(blank()); setNewCat("");
      await load(); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save the entry."); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this manual entry?")) return;
    try { await api.delete(`/admin/finance/entries/${id}`); await load(); onChanged?.(); toast.success("Entry removed."); }
    catch { toast.error("Couldn't remove it."); }
  };

  const input = "h-10 px-3 border text-sm bg-white w-full";
  const label = "block text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E1E3A] mb-1";
  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-4" data-testid="finance-entries">
      <form onSubmit={save} className="bg-white border p-4 space-y-3 self-start" style={{ borderColor: "#E8DFCB" }} data-testid="finance-entry-form">
        <h2 className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Add income or expense</h2>
        <div className="flex gap-1">
          {["expense", "income"].map((t) => (
            <button key={t} type="button" onClick={() => setForm((f) => ({ ...f, type: t, category: "" }))} data-testid={`entry-type-${t}`}
              className={`flex-1 h-9 text-[11px] tracking-[0.18em] uppercase font-semibold border ${form.type === t ? (t === "income" ? "bg-[#2F6B4F] border-[#2F6B4F] text-white" : "bg-[#A14C4C] border-[#A14C4C] text-white") : "border-[#E8DFCB] text-[#5A4565]"}`}>{t}</button>
          ))}
        </div>
        <label className="block"><span className={label}>Category</span>
          <select value={form.category} onChange={set("category")} className={input} style={{ borderColor: "#E8DFCB" }} data-testid="entry-category">
            <option value="">Choose…</option>
            {(cats[form.type] || []).map((c) => <option key={c} value={c}>{c}</option>)}
            <option value="__new__">+ New category…</option>
          </select></label>
        {form.category === "__new__" && (
          <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" className={input} style={{ borderColor: "#E8DFCB" }} data-testid="entry-new-category" />
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className={label}>Amount (NZ$)</span><input type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className={input} style={{ borderColor: "#E8DFCB" }} data-testid="entry-amount" required /></label>
          <label className="block"><span className={label}>Date</span><input type="date" value={form.date} onChange={set("date")} className={input} style={{ borderColor: "#E8DFCB" }} data-testid="entry-date" required /></label>
        </div>
        <label className="block"><span className={label}>Paid to / from</span><input value={form.party} onChange={set("party")} placeholder="e.g. NZ Post, Bunnings" className={input} style={{ borderColor: "#E8DFCB" }} data-testid="entry-party" /></label>
        <label className="block"><span className={label}>Note</span><input value={form.note} onChange={set("note")} placeholder="What was it for?" className={input} style={{ borderColor: "#E8DFCB" }} data-testid="entry-note" /></label>
        <button type="submit" disabled={busy} className="w-full h-10 inline-flex items-center justify-center gap-2 text-white text-[11px] tracking-[0.2em] uppercase font-semibold disabled:opacity-50" style={{ backgroundColor: "#3E1F46" }} data-testid="entry-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Save entry
        </button>
      </form>

      <section className="bg-white border" style={{ borderColor: "#E8DFCB" }}>
        <h2 className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] px-4 py-3 border-b" style={{ borderColor: "#F3EBDD" }}>Manual entries</h2>
        {!entries ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-[#6E1E3A]" /></div> : entries.length === 0 ? (
          <p className="px-4 py-10 text-center italic text-[#9C8B73] text-sm">Nothing recorded yet. Add postage, supplies, wages or one-off income on the left.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-[9px] tracking-[0.25em] uppercase text-[#6E1E3A] text-left"><th className="px-4 py-2">Date</th><th className="px-2 py-2">Category</th><th className="px-2 py-2">Paid to / from</th><th className="px-2 py-2">Note</th><th className="px-2 py-2 text-right">Amount</th><th className="px-2 py-2" /></tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t" style={{ borderColor: "#F3EBDD" }} data-testid={`entry-row-${e.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap text-[#7A6A55]">{e.date}</td>
                  <td className="px-2 py-2"><span className={`px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase font-semibold ${e.type === "income" ? "bg-[#E4F0E8] text-[#2F6B4F]" : "bg-[#FBF0F0] text-[#A14C4C]"}`}>{e.category}</span></td>
                  <td className="px-2 py-2">{e.party || <span className="text-[#9C8B73]">—</span>}</td>
                  <td className="px-2 py-2 text-[#5A4565] max-w-[260px] truncate">{e.note}</td>
                  <td className={`px-2 py-2 text-right font-semibold ${e.type === "income" ? "text-[#2F6B4F]" : "text-[#A14C4C]"}`}>{e.type === "income" ? "+" : "−"}{money(e.amount)}</td>
                  <td className="px-2 py-2 text-right"><button type="button" onClick={() => remove(e.id)} className="text-[#A33030] hover:opacity-70" data-testid={`entry-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
